import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { AppLayoutWrapper } from '../layout/AppLayoutWrapper';
import { ClientPortalLayout } from '../layout/ClientPortalLayout';

import AdminLogin from '../../pages/AdminLogin';
import AdminDashboard from '../../pages/AdminDashboard';
import ClientLoginPage from '../../pages/ClientLoginPage';
import ClientForcePasswordChangePage from '../../pages/ClientForcePasswordChangePage';
import ClientForgotPasswordPage from '../../pages/ClientForgotPasswordPage';
import ClientResetPasswordPage from '../../pages/ClientResetPasswordPage';
import ClientAccountPasswordPage from '../../pages/ClientAccountPasswordPage';

import { UserManagementOverlay } from '../admin-overlays/UserManagementOverlay';
import { TransactionMonitoringOverlay } from '../admin-overlays/TransactionMonitoringOverlay';
import { FicTfsOverlay } from '../admin-overlays/FicTfsOverlay';
import { CatalogGovernanceOverlay } from '../admin-overlays/CatalogGovernanceOverlay';
import { SupportKnowledgeOverlay } from '../admin-overlays/SupportKnowledgeOverlay';
import { PartnerOnboardingOverlay } from '../admin-overlays/PartnerOnboardingOverlay';
import { FeeScheduleOverlay } from '../admin-overlays/FeeScheduleOverlay';
import { PartnerSettlementOverlay } from '../admin-overlays/PartnerSettlementOverlay';
import { AuditEvidenceOverlay } from '../admin-overlays/AuditEvidenceOverlay';
import { SupplierStatementsOverlay } from '../admin-overlays/SupplierStatementsOverlay';
// NOTE: Float, Settlements, Services, System Configuration, Security/Audit and
// Reports overlays are placeholder ("Coming Soon") screens. They are
// intentionally NOT routed in this production build — the paths redirect to the
// dashboard (see HIDDEN_PLACEHOLDER_PATHS below) until the real screens ship.
import { UnallocatedDepositsOverlay } from '../admin-overlays/UnallocatedDepositsOverlay';
import { CashInSettlementOverlay } from '../admin-overlays/CashInSettlementOverlay';
import { DisbursementRunsOverlay } from '../admin-overlays/DisbursementRunsOverlay';
import { CreateDisbursementRunOverlay } from '../admin-overlays/CreateDisbursementRunOverlay';
import { DisbursementRunDetailOverlay } from '../admin-overlays/DisbursementRunDetailOverlay';
import { ChannelPartnersOverlay } from '../admin-overlays/ChannelPartnersOverlay';
import { MmspClubsOverlay } from '../admin-overlays/MmspClubsOverlay';
import { MmspClubDetailOverlay } from '../admin-overlays/MmspClubDetailOverlay';
import { AdminCpMirrorProvider } from '../admin-overlays/AdminCpMirrorProvider';
import { DisbursementClientDetailOverlay } from '../admin-overlays/DisbursementClientDetailOverlay';
import {
  LegacyCpMirrorPathRedirect,
  LegacyCpMirrorRunDetailRedirect,
  LegacyDisbursementClientDetailRedirect,
  LegacyDisbursementClientsListRedirect,
  LegacyFinancePageRedirect,
} from './LegacyAdminRouteRedirects';
import { TreasuryOutboundDebitEventsOverlay } from '../admin-overlays/TreasuryOutboundDebitEventsOverlay';
import { SystemHealthOverlay } from '../admin-overlays/SystemHealthOverlay';
import { DatabaseHealthOverlay } from '../admin-overlays/platform-health/DatabaseHealthOverlay';
import { ServicesHealthOverlay } from '../admin-overlays/platform-health/ServicesHealthOverlay';
import { SupplierFloatDetailOverlay } from '../admin-overlays/SupplierFloatDetailOverlay';

import { ClientDashboardOverlay } from '../client-portal/ClientDashboardOverlay';
import { ClientRunsOverlay } from '../client-portal/ClientRunsOverlay';
import { ClientTeamOverlay } from '../client-portal/ClientTeamOverlay';
import { ClientRunDetailOverlay } from '../client-portal/ClientRunDetailOverlay';
import { ClientUploadOverlay } from '../client-portal/ClientUploadOverlay';
import { ClientOnboardingOverlay } from '../client-portal/ClientOnboardingOverlay';
import { ClientAttributedUsersOverlay } from '../client-portal/ClientAttributedUsersOverlay';
import { ClientTransactionReportsOverlay } from '../client-portal/ClientTransactionReportsOverlay';
import { ClientFinanceOverlay, RedirectStatementsToFinance } from '../client-portal/ClientFinanceOverlay';
import { ClientWelcomeEmailOverlay } from '../client-portal/ClientWelcomeEmailOverlay';
import { AgreementCenterOverlay } from '../client-portal/AgreementCenterOverlay';
import { CredentialsCenterOverlay } from '../client-portal/CredentialsCenterOverlay';
import { ApiDocsOverlay } from '../client-portal/ApiDocsOverlay';
import { clientPortalHomePath } from '../../utils/clientPortalPaths';

// Admin portal is staff-only and banking-grade: only admin/manager portal users
// may reach any admin screen. Other authenticated roles (user/viewer) are
// bounced to login rather than shown an operational surface.
const ADMIN_ALLOWED_ROLES = ['admin', 'manager'];

// Placeholder ("Coming Soon") screens hidden from this production build. Direct
// navigation to these paths redirects authenticated staff back to the dashboard.
const HIDDEN_PLACEHOLDER_PATHS = [
  '/admin/floats',
  '/admin/settlements',
  '/admin/services',
  '/admin/security',
  '/admin/reports',
];

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--background)]">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
          <p className="text-sm text-[var(--muted-foreground)]">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (user && user.role && !ADMIN_ALLOWED_ROLES.includes(user.role)) {
    return <Navigate to="/admin/login" replace />;
  }

  return <Outlet />;
};

const ClientProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading, mustChangePassword } = useClientAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--background)]">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
          <p className="text-sm text-[var(--muted-foreground)]">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/client/login" replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/client/change-password" replace />;
  }

  return <div className="h-screen"><Outlet /></div>;
};

const ClientPasswordChangeRoute: React.FC = () => {
  const { isAuthenticated, isLoading, mustChangePassword, user } = useClientAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--background)]">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/client/login" replace />;
  }

  if (!mustChangePassword) {
    return <Navigate to={clientPortalHomePath(user)} replace />;
  }

  return <Outlet />;
};

const ClientOperationalRoute: React.FC = () => {
  const { user, isLoading } = useClientAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--background)]">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
      </div>
    );
  }

  if (user?.onboardingPhase !== 'operational') {
    // Non-operational CPs land on the Agreement Center first (NDA → CPA →
    // annexures), mirroring the ClientLoginPage redirect. Agreements must be
    // accepted before the onboarding form can be submitted (backend gate).
    return <Navigate to="/client/agreements" replace />;
  }

  return <Outlet />;
};

const ClientPassthroughRoute: React.FC = () => {
  const { user, isLoading } = useClientAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--background)]">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-[var(--muted)] border-t-[var(--primary)]" />
      </div>
    );
  }

  if (!user?.passthroughEnabled) {
    return <Navigate to="/client/dashboard" replace />;
  }

  return <Outlet />;
};

export const RouteConfig: React.FC = () => {
  return (
    <Routes>
      {/* Public */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/client/login" element={<ClientLoginPage />} />
      <Route path="/client/forgot-password" element={<ClientForgotPasswordPage />} />
      <Route path="/client/reset-password" element={<ClientResetPasswordPage />} />
      <Route path="/client/account/password" element={<ClientAccountPasswordPage />} />

      <Route element={<ClientPasswordChangeRoute />}>
        <Route path="/client/change-password" element={<ClientForcePasswordChangePage />} />
      </Route>

      {/* Protected admin routes — single AppLayoutWrapper shell */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayoutWrapper />}>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<UserManagementOverlay />} />
          <Route path="/admin/partners" element={<PartnerOnboardingOverlay />} />
          <Route path="/admin/transactions" element={<TransactionMonitoringOverlay />} />
          <Route path="/admin/fic-tfs" element={<FicTfsOverlay />} />
          <Route path="/admin/catalog-governance" element={<CatalogGovernanceOverlay />} />
          <Route path="/admin/support-kb" element={<SupportKnowledgeOverlay />} />
          <Route path="/admin/treasury-outbound-debits" element={<TreasuryOutboundDebitEventsOverlay />} />
          <Route path="/admin/unallocated-deposits" element={<UnallocatedDepositsOverlay />} />
          <Route path="/admin/cash-in-settlement" element={<CashInSettlementOverlay />} />
          <Route path="/admin/disbursements" element={<DisbursementRunsOverlay />} />
          <Route path="/admin/disbursements/create" element={<CreateDisbursementRunOverlay />} />
          <Route path="/admin/disbursements/:id" element={<DisbursementRunDetailOverlay />} />
          <Route path="/admin/channel-partners" element={<ChannelPartnersOverlay />} />
          <Route path="/admin/channel-partners/:clientId" element={<DisbursementClientDetailOverlay />} />
          <Route path="/admin/partner-settlement" element={<PartnerSettlementOverlay />} />
          <Route path="/admin/audit-evidence" element={<AuditEvidenceOverlay />} />
          <Route path="/admin/finance" element={<LegacyFinancePageRedirect />} />
          <Route path="/admin/supplier-statements" element={<SupplierStatementsOverlay />} />
          <Route path="/admin/mmsp" element={<MmspClubsOverlay />} />
          <Route path="/admin/mmsp/:clubId" element={<MmspClubDetailOverlay />} />
          <Route path="/admin/disbursement-clients" element={<LegacyDisbursementClientsListRedirect />} />
          <Route path="/admin/disbursement-clients/:clientId" element={<LegacyDisbursementClientDetailRedirect />} />

          <Route path="/admin/system" element={<SystemHealthOverlay />} />
          <Route path="/admin/system/database" element={<DatabaseHealthOverlay />} />
          <Route path="/admin/system/services" element={<ServicesHealthOverlay />} />
          <Route path="/admin/fee-schedules" element={<FeeScheduleOverlay />} />
          <Route path="/admin/floats/:supplierId" element={<SupplierFloatDetailOverlay />} />

          {/* Hidden placeholder screens — redirect to dashboard until built */}
          {HIDDEN_PLACEHOLDER_PATHS.map((p) => (
            <Route key={p} path={p} element={<Navigate to="/admin/dashboard" replace />} />
          ))}
        </Route>

        {/* MM Admin mirror of CP portal — full-screen (no admin sidebar) */}
        <Route path="/admin/channel-partners/:clientId/portal" element={<AdminCpMirrorProvider />}>
          <Route element={<ClientPortalLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<ClientDashboardOverlay />} />
            <Route path="users" element={<ClientAttributedUsersOverlay />} />
            <Route path="reports" element={<ClientTransactionReportsOverlay />} />
            <Route path="finance" element={<ClientFinanceOverlay />} />
            <Route path="statements" element={<RedirectStatementsToFinance />} />
            <Route path="welcome-email" element={<ClientWelcomeEmailOverlay />} />
            <Route path="runs" element={<ClientRunsOverlay />} />
            <Route path="runs/:id" element={<ClientRunDetailOverlay />} />
            <Route path="upload" element={<ClientUploadOverlay />} />
            <Route path="team" element={<ClientTeamOverlay />} />
          </Route>
        </Route>

        {/* Legacy mirror URLs (pre-Phase-A) */}
        <Route path="/admin/channel-partners/:clientId/dashboard" element={<LegacyCpMirrorPathRedirect tail="dashboard" />} />
        <Route path="/admin/channel-partners/:clientId/users" element={<LegacyCpMirrorPathRedirect tail="users" />} />
        <Route path="/admin/channel-partners/:clientId/welcome-email" element={<LegacyCpMirrorPathRedirect tail="welcome-email" />} />
        <Route path="/admin/channel-partners/:clientId/runs/:id" element={<LegacyCpMirrorRunDetailRedirect />} />
        <Route path="/admin/channel-partners/:clientId/runs" element={<LegacyCpMirrorPathRedirect tail="runs" />} />
        <Route path="/admin/channel-partners/:clientId/upload" element={<LegacyCpMirrorPathRedirect tail="upload" />} />
        <Route path="/admin/channel-partners/:clientId/team" element={<LegacyCpMirrorPathRedirect tail="team" />} />
      </Route>

      {/* Protected client portal routes */}
      <Route element={<ClientProtectedRoute />}>
        <Route path="/client/onboarding" element={<ClientOnboardingOverlay />} />
        <Route element={<ClientPortalLayout />}>
          <Route path="/client/users" element={<ClientAttributedUsersOverlay />} />
          <Route path="/client/reports" element={<ClientTransactionReportsOverlay />} />
          <Route path="/client/finance" element={<ClientFinanceOverlay />} />
          <Route path="/client/statements" element={<RedirectStatementsToFinance />} />
          <Route path="/client/agreements" element={<AgreementCenterOverlay />} />
          <Route element={<ClientOperationalRoute />}>
            <Route path="/client/dashboard" element={<ClientDashboardOverlay />} />
            <Route element={<ClientPassthroughRoute />}>
              <Route path="/client/credentials" element={<CredentialsCenterOverlay />} />
              <Route path="/client/api-docs" element={<ApiDocsOverlay />} />
            </Route>
            <Route path="/client/runs" element={<ClientRunsOverlay />} />
            <Route path="/client/runs/:id" element={<ClientRunDetailOverlay />} />
            <Route path="/client/upload" element={<ClientUploadOverlay />} />
            <Route path="/client/welcome-email" element={<ClientWelcomeEmailOverlay />} />
            <Route path="/client/team" element={<ClientTeamOverlay />} />
          </Route>
        </Route>
      </Route>

      {/* Fallback redirects */}
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      <Route path="/client" element={<Navigate to="/client/login" replace />} />
      {/* Partner links must never land on the staff login (its error reads "Invalid email or password"). */}
      <Route path="/client/*" element={<Navigate to="/client/login" replace />} />
      <Route path="/" element={<Navigate to="/admin/login" replace />} />
      <Route path="*" element={<Navigate to="/admin/login" replace />} />
    </Routes>
  );
};
